import { useEffect, useState } from 'react'

import classnames from 'classnames'

import type { AccordionItemProps } from '~/components/atoms/AccordionItem/AccordionItem'
import AccordionItem from '~/components/atoms/AccordionItem/AccordionItem'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'

import styles from './Accordion.module.css'

export interface AccordionProps {
  data: AccordionItemProps[]
  independentItems?: boolean
  title?: string
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  noContainer?: boolean
  firstItemOpen?: boolean
  faqAsComponent?: boolean
  fullWidth?: boolean
  moduleId?: string
  variant?: null | 'magazine'
  noPaddingTop?: boolean
}

export default function Accordion({
  data,
  independentItems = true,
  title,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  testID,
  noContainer,
  firstItemOpen,
  faqAsComponent = true,
  moduleId,
  fullWidth = false,
  variant,
  noPaddingTop = false,
}: AccordionProps) {
  if (!faqAsComponent) return <></>

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [firstOpen, setFirstOpen] = useState<boolean>(false)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (firstItemOpen) setFirstOpen(true)
  }, [firstItemOpen])

  const handleClick = (idx: number) => {
    if (independentItems) return
    const newData = [...data]
    setFirstOpen(false)
    newData.forEach((item, i) => {
      item.isOpen = i === idx
    })
  }

  const RenderAccordion = () => {
    return (
      <div
        className={classnames(
          styles.accordion,
          noContainer && styles.accordionFullWidth,
          fullWidth && styles.accordionFullWidth
        )}
        id={moduleId}
      >
        {title && (
          <Text
            Tag="h2"
            size={TextSizes.primaryTitleLrg}
            className={styles.title}
          >
            {title}
          </Text>
        )}
        <ul aria-label="Accordion Control Group Buttons">
          {data &&
            data.map((accordion: any, idx: number) => (
              <AccordionItem
                key={accordion.title}
                isOpen={firstOpen && idx === 0 ? true : accordion?.isOpen}
                {...accordion}
                onClick={() => handleClick(idx)}
                testID={testID && `accordion-${testID}`}
                className={styles.row}
                _key={accordion._key}
              />
            ))}
        </ul>
      </div>
    )
  }

  return (
    <Fixture
      noPaddingTop={noPaddingTop}
      verticalPadding={verticalPadding}
      theme={theme}
      testID={testID}
      noPaddingBottom={noPaddingBottom}
    >
      {noContainer ? (
        RenderAccordion()
      ) : (
        <Container>
          <div
            className={classnames({
              [styles.magazine]: variant === 'magazine',
            })}
          >
            <Grid>
              <Row>
                <Col>{RenderAccordion()}</Col>
              </Row>
            </Grid>
          </div>
        </Container>
      )}
    </Fixture>
  )
}
